import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  dialogTitleClasses,
} from '@mui/material';
import {
  BugReport as JiraIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Lock as LockIcon,
  Save as SaveIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { TableColumn } from '../types/table.types';
import { JiraService } from '../services/jira.service';
import {
  JIRA_ISSUE_FIELDS,
  JIRA_TRANSFORMS,
  JiraPreset,
  JiraPushConfig,
  JiraPushField,
  JiraPushResult,
} from '../types/jira.types';

const ACCENT = '#0052cc';

interface JiraPushDialogProps {
  open: boolean;
  onClose: () => void;
  tableId: string;
  dataId: string | null;
  row: Record<string, any> | null;
  columns: TableColumn[];
  canEdit: boolean;
}

const SKIP = '__skip__';
const CONST = '__const__';

const HEAD_CELL_SX = {
  fontWeight: 700,
  bgcolor: 'background.paper',
  backgroundImage: 'linear-gradient(rgba(0,82,204,0.06), rgba(0,82,204,0.06))',
};

const sameField = (a?: JiraPushField, b?: JiraPushField): boolean => {
  if (!a || !b) return !a && !b;
  return a.src === b.src
    && a.value === b.value
    && (a.transform || '') === (b.transform || '')
    && Boolean(a.encrypt) === Boolean(b.encrypt);
};

export default function JiraPushDialog({
  open,
  onClose,
  tableId,
  dataId,
  row,
  columns,
  canEdit,
}: JiraPushDialogProps) {
  const [presets, setPresets] = useState<JiraPreset[]>([]);
  const [presetId, setPresetId] = useState<string>('');
  const [presetName, setPresetName] = useState('');
  const [fields, setFields] = useState<Record<string, JiraPushField>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<JiraPushResult | null>(null);

  const selectedPreset = useMemo(
    () => presets.find(p => p.id === presetId) || null,
    [presets, presetId],
  );

  const isDirty = useMemo(() => {
    if (!selectedPreset) return Object.keys(fields).length > 0;
    if (selectedPreset.name !== presetName.trim()) return true;
    const saved = (selectedPreset.config as JiraPushConfig)?.fields || {};
    return Object.keys({ ...saved, ...fields }).some(name => !sameField(saved[name], fields[name]));
  }, [selectedPreset, fields, presetName]);

  const loadPresets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const all = await JiraService.getPresets(tableId);
      const pushPresets = all.filter(p => p.type === 'push');
      setPresets(pushPresets);
      if (pushPresets.length > 0) {
        setPresetId(pushPresets[0].id);
        setPresetName(pushPresets[0].name);
        setFields({ ...((pushPresets[0].config as JiraPushConfig)?.fields || {}) });
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Не удалось загрузить пресеты');
    } finally {
      setLoading(false);
    }
  }, [tableId]);

  useEffect(() => {
    if (!open) return;
    setPresets([]);
    setPresetId('');
    setPresetName('');
    setFields({});
    setResult(null);
    setError(null);
    loadPresets();
  }, [open, loadPresets]);

  const handleSelectPreset = (id: string) => {
    setPresetId(id);
    setResult(null);
    const preset = presets.find(p => p.id === id);
    setPresetName(preset?.name || '');
    setFields({ ...((preset?.config as JiraPushConfig)?.fields || {}) });
  };

  const handleNewPreset = () => {
    setPresetId('');
    setPresetName('');
    setFields({});
    setResult(null);
  };

  const handleSourceChange = (jiraField: string, value: string) => {
    setFields(prev => {
      const next = { ...prev };
      if (value === SKIP) {
        delete next[jiraField];
        return next;
      }
      const meta = JIRA_ISSUE_FIELDS.find(f => f.name === jiraField);
      const existing = prev[jiraField];
      next[jiraField] = {
        src: value === CONST ? 'const' : 'column',
        value: value === CONST ? (existing?.src === 'const' ? existing.value : '') : value,
        transform: existing?.transform ?? meta?.transform,
        encrypt: existing?.encrypt ?? Boolean(meta?.sensitive),
      };
      return next;
    });
  };

  const patchField = (jiraField: string, patch: Partial<JiraPushField>) => {
    setFields(prev => {
      if (!prev[jiraField]) return prev;
      return { ...prev, [jiraField]: { ...prev[jiraField], ...patch } };
    });
  };

  const buildConfig = (): JiraPushConfig => {
    const cleaned: Record<string, JiraPushField> = {};
    for (const [name, f] of Object.entries(fields)) {
      cleaned[name] = {
        src: f.src,
        value: f.value,
        ...(f.transform ? { transform: f.transform } : {}),
        ...(f.encrypt ? { encrypt: true } : {}),
      };
    }
    return { fields: cleaned };
  };

  const handleSave = async (asNew: boolean) => {
    if (!presetName.trim()) {
      setError('Введите название пресета');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const config = buildConfig();
      const saved = asNew || !presetId
        ? await JiraService.createPreset(tableId, { name: presetName.trim(), type: 'push', config })
        : await JiraService.updatePreset(tableId, presetId, { name: presetName.trim(), config });

      setPresets(prev => {
        const rest = prev.filter(p => p.id !== saved.id);
        return [...rest, saved].sort((a, b) => a.name.localeCompare(b.name));
      });
      setPresetId(saved.id);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Не удалось сохранить пресет');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!presetId) return;
    setSaving(true);
    setError(null);
    try {
      await JiraService.deletePreset(tableId, presetId);
      const rest = presets.filter(p => p.id !== presetId);
      setPresets(rest);
      if (rest.length > 0) handleSelectPreset(rest[0].id);
      else handleNewPreset();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Не удалось удалить пресет');
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async () => {
    if (!dataId || !presetId) return;
    setSending(true);
    setError(null);
    setResult(null);
    try {
      setResult(await JiraService.push(tableId, dataId, presetId));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Jira не приняла запрос');
    } finally {
      setSending(false);
    }
  };

  const previewValue = (jiraField: string): string => {
    const f = fields[jiraField];
    if (!f) return '—';
    if (f.src === 'const') return f.value || '(не задано)';
    const value = row?.[f.value];
    if (value === undefined || value === null || value === '') return '(пусто)';
    if (f.encrypt) return '••••••••';
    return String(value);
  };

  const mappedCount = Object.keys(fields).length;
  const hasKey = Boolean(fields.key);

  return (
    <Dialog
      open={open}
      onClose={sending ? undefined : onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle component="div" sx={{ background: `linear-gradient(135deg, ${ACCENT} 0%, #2684ff 100%)`, color: 'white', pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <JiraIcon sx={{ flexShrink: 0 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>Передать в Jira</Typography>
          {selectedPreset && (
            <Chip
              label={selectedPreset.name}
              title={selectedPreset.name}
              size="small"
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', minWidth: 0 }}
            />
          )}
        </Box>
      </DialogTitle>

      <DialogContent sx={{ minHeight: 320, [`.${dialogTitleClasses.root} + &`]: { pt: 3 } }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: ACCENT }} />
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <FormControl size="small" sx={{ minWidth: 260, maxWidth: 360 }}>
                <Select
                  value={presetId || ''}
                  displayEmpty
                  onChange={(e) => (e.target.value ? handleSelectPreset(e.target.value) : handleNewPreset())}
                >
                  <MenuItem value="">
                    <em>{canEdit ? 'Новый пресет' : 'Пресет не выбран'}</em>
                  </MenuItem>
                  {presets.map(p => (
                    <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {canEdit && (
                <>
                  <TextField
                    size="small"
                    label="Название пресета"
                    value={presetName}
                    onChange={(e) => setPresetName(e.target.value)}
                    sx={{ minWidth: 220 }}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<SaveIcon />}
                    disabled={saving || !presetName.trim()}
                    onClick={() => handleSave(false)}
                    sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                  >
                    {presetId ? 'Сохранить' : 'Создать'}
                  </Button>
                  {presetId && (
                    <>
                      <Button
                        size="small"
                        variant="text"
                        disabled={saving}
                        onClick={() => handleSave(true)}
                        sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                      >
                        Сохранить как новый
                      </Button>
                      <Tooltip title="Удалить пресет">
                        <span>
                          <IconButton size="small" color="error" disabled={saving} onClick={handleDelete}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </>
                  )}
                </>
              )}
            </Box>

            {!canEdit && (
              <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                Выберите пресет и отправьте строку. Менять сопоставление полей могут администраторы и PM.
              </Alert>
            )}

            {canEdit && isDirty && presetId && (
              <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                Изменения не сохранены. В Jira уйдёт сохранённая версия пресета — нажмите «Сохранить».
              </Alert>
            )}

            {!hasKey && (
              <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                Не задано поле «Ключ задачи» — без него Jira не поймёт, куда писать.
              </Alert>
            )}

            <TableContainer sx={{ maxHeight: 440 }}>
              <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', minWidth: 880 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...HEAD_CELL_SX, width: '20%' }}>Поле Jira</TableCell>
                    <TableCell sx={{ ...HEAD_CELL_SX, width: '24%' }}>Источник</TableCell>
                    <TableCell sx={{ ...HEAD_CELL_SX, width: '22%' }}>Преобразование</TableCell>
                    <TableCell sx={{ ...HEAD_CELL_SX, width: 120 }} align="center">Шифровать</TableCell>
                    <TableCell sx={HEAD_CELL_SX}>Уйдёт в Jira</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {JIRA_ISSUE_FIELDS.map(meta => {
                    const f = fields[meta.name];
                    const selectValue = !f ? SKIP : f.src === 'const' ? CONST : f.value;
                    const preview = previewValue(meta.name);

                    return (
                      <TableRow key={meta.name} hover>
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{meta.label}</Typography>
                            {meta.required && <Chip label="*" size="small" color="error" sx={{ height: 18, fontSize: '0.65rem', flexShrink: 0 }} />}
                          </Stack>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
                            {meta.name}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Stack spacing={0.5}>
                            <FormControl fullWidth size="small">
                              <Select
                                value={selectValue}
                                disabled={!canEdit}
                                onChange={(e) => handleSourceChange(meta.name, e.target.value)}
                              >
                                <MenuItem value={SKIP}><em>Не передавать</em></MenuItem>
                                {columns.map(c => (
                                  <MenuItem key={c.id} value={c.name}>{c.name}</MenuItem>
                                ))}
                                <MenuItem value={CONST}>Своё значение…</MenuItem>
                              </Select>
                            </FormControl>
                            {f?.src === 'const' && (
                              <TextField
                                size="small"
                                fullWidth
                                placeholder="Значение"
                                value={f.value}
                                disabled={!canEdit}
                                onChange={(e) => patchField(meta.name, { value: e.target.value })}
                              />
                            )}
                          </Stack>
                        </TableCell>

                        <TableCell>
                          <FormControl fullWidth size="small">
                            <Select
                              value={f?.transform || ''}
                              displayEmpty={Boolean(f)}
                              disabled={!canEdit || !f}
                              onChange={(e) => patchField(meta.name, { transform: (e.target.value || undefined) as any })}
                            >
                              {JIRA_TRANSFORMS.map(t => (
                                <MenuItem key={t.value || 'asis'} value={t.value}>{t.label}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>

                        <TableCell align="center">
                          <Checkbox
                            size="small"
                            checked={Boolean(f?.encrypt)}
                            disabled={!canEdit || !f}
                            onChange={(e) => patchField(meta.name, { encrypt: e.target.checked })}
                            sx={{ color: ACCENT, '&.Mui-checked': { color: ACCENT } }}
                          />
                        </TableCell>

                        <TableCell>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            {f?.encrypt && <LockIcon sx={{ fontSize: 14, color: ACCENT, flexShrink: 0 }} />}
                            <Typography variant="caption" color="text.secondary" noWrap title={preview} sx={{ minWidth: 0 }}>
                              {preview}
                            </Typography>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Divider sx={{ my: 2 }} />

            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                label={`Полей к отправке: ${mappedCount}`}
                sx={{ bgcolor: 'rgba(0,82,204,0.08)', color: ACCENT, fontWeight: 600 }}
              />
              <Chip
                icon={<LockIcon />}
                label={`Шифруется: ${Object.values(fields).filter(f => f.encrypt).length}`}
                sx={{ bgcolor: 'rgba(76,175,80,0.1)', color: '#2e7d32', fontWeight: 600 }}
              />
            </Stack>

            {error && <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{error}</Alert>}

            {result && (
              <Alert
                severity="success"
                icon={<CheckCircleIcon fontSize="inherit" />}
                sx={{ mt: 2, borderRadius: 2 }}
              >
                Отправлено в задачу <strong>{result.key}</strong>: {result.sentFields.length} полей,
                из них зашифровано {result.encryptedFields.length}.
                {result.skipped?.length > 0 && (
                  <Box sx={{ mt: 0.5 }}>
                    <Typography variant="caption">
                      Пропущены (пусто или значение не преобразуется): {result.skipped.join(', ')}
                    </Typography>
                  </Box>
                )}
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions disableSpacing sx={{ p: 3, pt: 0, gap: 1, flexWrap: 'wrap' }}>
        <Button onClick={onClose} disabled={sending}>Закрыть</Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={sending || !presetId || !dataId}
          startIcon={sending ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
          sx={{
            background: `linear-gradient(135deg, ${ACCENT} 0%, #2684ff 100%)`,
            '&:hover': { background: 'linear-gradient(135deg, #003d99 0%, #1a73e8 100%)' },
          }}
        >
          {sending ? 'Отправка…' : 'Отправить в Jira'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}